import {useCallback, useMemo, useRef} from 'react';
import {RestAPI} from '../services/rest-api';
import {RestClient} from '../services/rest-client';
import {useMutation, useQuery} from '@tanstack/react-query';
import useConfig from './useConfig';

export const signerStatusList = [
  'CREATED',
  'COMPLETED',
  'REJECTED',
  'EXPIRED',
  'UNKNOWN',
  'SIGNER_MISMATCH',
  'CANCELLED_VIA_API',
] as const;

export type SignerStatus = (typeof signerStatusList)[number];

const TERMINAL_STATUSES: SignerStatus[] = [
  'COMPLETED',
  'REJECTED',
  'EXPIRED',
  'UNKNOWN',
  'SIGNER_MISMATCH',
  'CANCELLED_VIA_API',
];

export interface SigningStatusResponse {
  status: SignerStatus;
  qrCode?: string;
  hintCode?: string;
  autoStartToken?: string;
  redirectUrl?: string;
  cancelRedirectUrl?: string;
  expiresAt?: string;
}

export const useSigningStatus = (
  sessionId: string | undefined,
  signerId: string | undefined,
  enabled: boolean,
  bft: string | undefined,
) => {
  const appConfig = useConfig();
  const api = useMemo(
    () =>
      new RestAPI(
        new RestClient({
          enableLog: appConfig.enableLog,
          apiKey: appConfig.apiKey,
        }),
      ),
    [appConfig.enableLog, appConfig.apiKey],
  );

  const query = useQuery({
    queryKey: ['signing', 'signer', 'status', sessionId, signerId, bft],
    enabled: enabled && Boolean(sessionId) && Boolean(signerId) && Boolean(bft),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status && TERMINAL_STATUSES.includes(status)) return false;
      return 2000;
    },
    queryFn: async () => {
      const url = `${appConfig.bankIdHost}/signing/${sessionId}/signer/${signerId}/status?bft=${encodeURIComponent(bft as string)}`;
      return await api.client.getJSONBody<SigningStatusResponse>(url);
    },
  });

  const cancel = useMutation({
    mutationKey: ['signing', 'signer', 'cancel', sessionId, signerId],
    mutationFn: async () => {
      const isDemo = appConfig.signingPathPrefix === '/mock/demo/signing';
      const url = isDemo
        ? `${appConfig.bankIdHost}${appConfig.signingPathPrefix}/${sessionId}`
        : `${appConfig.bankIdHost}${appConfig.signingPathPrefix}/${sessionId}/signer/${signerId}`;
      return await api.client.fetchJSONBody('DELETE', url);
    },
  });

  const initializeSession = useMutation({
    mutationKey: ['signing', 'signer', 'init', sessionId, signerId, bft],
    mutationFn: async (): Promise<{autoStartToken: string | null}> => {
      if (!sessionId || !signerId)
        throw new Error('Session and signer required to initialize signing');
      if (!bft) throw new Error('BFT required to initialize signing');
      const url = `${appConfig.bankIdHost}/signing/${sessionId}/signer/${signerId}/init`;
      const response = await api.client.fetchJSONBody<
        {autoStartToken?: string} | undefined
      >('POST', url, {bft});
      return {autoStartToken: response?.autoStartToken ?? null};
    },
  });

  const initializeSessionRef = useRef(initializeSession.mutateAsync);
  // eslint-disable-next-line react-hooks/refs
  initializeSessionRef.current = initializeSession.mutateAsync;
  const initializeSessionAsync = useCallback(
    () => initializeSessionRef.current(),
    [],
  );

  const isTerminal = TERMINAL_STATUSES.includes(
    query.data?.status as SignerStatus,
  );

  return {query, cancel, initializeSession, initializeSessionAsync, isTerminal};
};
